<template>
  <footer v-if="isVotingOpen && allowVoteRole" class="bg-light pb-5">
    <!-- Hint Text -->
    <div class="container d-flex justify-content-center" style="color: grey">
      <p>Click card to select vote</p>
    </div>

    <!-- Buttons -->
    <div class="container d-flex justify-content-center" style="gap: 0.5rem;">
      <!-- Vote Button -->
      <CButton
          color="success"
          shape="pill"
          size="md"
          class="vote-btn d-flex justify-content-center align-items-center gap-2"
          :disabled="voteState === 'voted' || voteState === 'abstained'"
          @click="onVote"
      >
        <span>✅</span>
        <span>Vote</span>
      </CButton>

      <!-- Abstention Button -->
      <CButton
          color="dark"
          shape="pill"
          size="md"
          class="vote-btn d-flex justify-content-center align-items-center gap-2"
          :disabled="voteState === 'voted' || voteState === 'abstained'"
          @click="onAbstention"
      >
        <span>🚫</span>
        <span>Abstain</span>
      </CButton>
    </div>
  </footer>
</template>

<script>
import Swal from 'sweetalert2'
import api from '@/service/api.js'

export default {
  name: 'Vote',

  props: {
    isVotingOpen: { type: Boolean, required: true },
    selectedCandidate: { type: [Number, String, Array], default: null },
    voteType: { type: String, default: '' },
    typeId: { type: String, default: '' },
    canVote: { type: Boolean, default: true }
  },

  data() {
    return {
      voteState: null,
      userRole: '',
      candidateName: ''
    }
  },

  computed: {
    allowVoteRole() {
      const loginSource = localStorage.getItem('loginSource');
      return (
          this.userRole === 'user' ||
          this.userRole === 'candidate' ||
          loginSource === 'student'
      );
    }
  },

  async mounted() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return this.$router.push({ name: 'Login' });

    const user = JSON.parse(userStr || '{}');
    this.userRole = user?.role?.role_name || '';

    await this.fetchVoteStatus();
    await this.fetchCandidateName();
  },

  methods: {
    async fetchVoteStatus() {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        const res = await api.vote('check', {
          people: user._id,
          type: this.typeId,
          year: new Date().getFullYear()
        });
        this.voteState = res?.data?.status || null;
      } catch (err) {
        console.warn('[Vote] failed to fetch vote status', err);
        this.voteState = null;
      }
    },

    async fetchCandidateName() {
      if (!this.selectedCandidate || Array.isArray(this.selectedCandidate) && this.selectedCandidate.length === 0) return;

      try {
        const res = await api.candidate('get');
        const candidates = res.data.data || [];
        const selectedIds = Array.isArray(this.selectedCandidate)
            ? this.selectedCandidate
            : [this.selectedCandidate];

        const selectedNames = candidates
            .filter(c => selectedIds.includes(c._id))
            .map(c => c.accountID?.name || 'Unknown');

        this.candidateName = selectedNames.join(', ');
      } catch (err) {
        console.warn('[Vote] failed to fetch candidate names');
      }
    },

    async onVote() {
      if (this.voteState === 'voted') {
        return Swal.fire({
          icon: 'info',
          title: 'You Already Voted',
          html: `You already voted for <strong>${this.candidateName || 'a candidate'}</strong>.`,
          confirmButtonText: 'OK'
        });
      }

      if (this.voteState === 'abstained') {
        return Swal.fire({
          icon: 'info',
          title: 'You Already Abstained',
          text: 'You have already abstained from this vote.',
          confirmButtonText: 'OK'
        });
      }

      const noSelection =
          !this.selectedCandidate ||
          (Array.isArray(this.selectedCandidate) && this.selectedCandidate.length === 0);

      if (noSelection) {
        return Swal.fire({
          icon: 'warning',
          title: 'No Candidate Selected',
          text: 'Please select a candidate before voting.',
          confirmButtonText: 'OK'
        });
      }

      try {
        const userStr = localStorage.getItem('user');
        const user = JSON.parse(userStr || '{}');
        const candidateIds = Array.isArray(this.selectedCandidate)
            ? this.selectedCandidate
            : [this.selectedCandidate];

        const payload = {
          people: user._id,
          candidate: candidateIds,
          type: this.typeId,
          year: new Date().getFullYear()
        };

        await api.vote('sendVote', payload);
        this.voteState = 'voted';

        await Swal.fire({
          icon: 'success',
          title: 'Vote Submitted!',
          text: `Thank you for voting for ${this.voteType || 'this position'}!`,
          confirmButtonText: 'OK'
        });
        this.$router.push({ name: 'Home' });
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: 'Vote Failed',
          text: err?.response?.data?.message || err.message || 'Something went wrong.',
          confirmButtonText: 'OK'
        });
      }
    },

    async onAbstention() {
      if (this.voteState === 'voted' || this.voteState === 'abstained') {
        return Swal.fire({
          icon: 'info',
          title: 'Already Submitted',
          text: 'You already submitted your vote or abstained.',
          confirmButtonText: 'OK'
        });
      }

      const result = await Swal.fire({
        icon: 'warning',
        title: 'Are you sure?',
        text: `You are choosing not to vote for ${this.voteType || 'this position'}.`,
        showCancelButton: true,
        confirmButtonText: 'Yes, abstain',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const payload = {
          people: user._id,
          type: this.typeId,
          year: new Date().getFullYear(),
          abstain: true
        };

        await api.vote('abstain', payload);
        this.voteState = 'abstained';

        await Swal.fire({
          icon: 'success',
          title: 'Recorded',
          text: 'Your abstention has been recorded.'
        });
        this.$router.push({ name: 'Home' });
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: 'Abstain Failed',
          text: err?.response?.data?.message || err.message
        });
      }
    }
  },

  watch: {
    typeId: {
      immediate: true,
      handler() {
        this.fetchVoteStatus();
      }
    },
    selectedCandidate: {
      immediate: true,
      handler() {
        this.fetchCandidateName();
      }
    }
  }
}
</script>

<style scoped>
.vote-btn {
  min-width: 130px;
  height: 44px;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.vote-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
footer {
  margin-top: 8px;
  padding-top: 8px;
}
</style>
